import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar.jsx";
import { t } from "../i18n";
import { fetchBillingStatus, fetchPlans, fetchTopups, startCheckout, startPortal, startTopupCheckout } from "../services/billing.js";
import { track } from "../services/analytics.js";
import "./Pricing.css";

export default function Pricing() {
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [topups, setTopups] = useState([]);
  const [error, setError] = useState("");
  const [plansError, setPlansError] = useState("");
  const [topupsError, setTopupsError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setBilling(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err.message || t("pricing.error.billing"));
      });
    fetchPlans()
      .then((data) => {
        if (!isMounted) return;
        setPlans(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setPlansError(err.message || t("pricing.error.plans"));
      });
    fetchTopups()
      .then((data) => {
        if (!isMounted) return;
        setTopups(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!isMounted) return;
        setTopupsError(err.message || t("pricing.error.topups"));
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const currentPlanId = billing?.plan_id || "free";
  const used = billing?.credits_used ?? 0;
  const included = billing?.credits_included ?? 0;
  const remaining = billing?.credits_remaining ?? 0;

  async function handleCheckout(planId) {
    setActionError("");
    setActionLoading(true);
    try {
      track("billing_checkout_started", { plan_id: planId });
      await startCheckout(planId);
    } catch (err) {
      setActionError(err.message || t("pricing.error.checkout"));
    } finally {
      setActionLoading(false);
    }
  }

  async function handlePortal() {
    setActionError("");
    setActionLoading(true);
    try {
      track("billing_portal_opened");
      await startPortal();
    } catch (err) {
      setActionError(err.message || t("pricing.error.portal"));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleTopup(packId) {
    setActionError("");
    setActionLoading(true);
    try {
      track("topup_started", { pack_id: packId });
      await startTopupCheckout(packId);
    } catch (err) {
      setActionError(err.message || t("pricing.error.checkout"));
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="app-shell">
      <Sidebar active="pricing" />
      <div className="app-main">
        <div className="pricing-page">
          <header className="pricing-header">
            <h1 className="pricing-title page-title-accent">{t("pricing.title")}</h1>
            {error && <div className="nano-alert error">{error}</div>}
            {actionError && <div className="nano-alert error">{actionError}</div>}
          </header>

          <section className="pricing-summary">
            <h2>{t("pricing.yourPlan")}</h2>
            <div className="pricing-summary-grid">
              <div className="pricing-summary-card">
                <span className="pricing-label">{t("pricing.plan")}</span>
                <strong>{currentPlanId}</strong>
              </div>
              <div className="pricing-summary-card">
                <span className="pricing-label">{t("pricing.creditsUsed")}</span>
                <strong>{used}</strong>
              </div>
              <div className="pricing-summary-card">
                <span className="pricing-label">{t("pricing.creditsRemaining")}</span>
                <strong>{remaining}</strong>
              </div>
              <div className="pricing-summary-card">
                <span className="pricing-label">{t("pricing.creditsIncluded")}</span>
                <strong>{included}</strong>
              </div>
            </div>
          </section>

          <section className="pricing-plans">
            <h2>{t("pricing.subscriptions")}</h2>
            {plansError && <div className="pricing-inline-error">{plansError}</div>}
            <div className="pricing-plan-grid">
              {plans
                .filter((plan) => plan.id === "starter" || plan.id === "pro")
                .map((plan) => {
                  const isCurrent = plan.id === currentPlanId;
                  return (
                    <div key={plan.id} className={`pricing-plan ${isCurrent ? "is-active" : ""}`}>
                      <h3>{plan.label || plan.id}</h3>
                      <p>{plan.monthly_credits} {t("pricing.creditsMonth")}</p>
                      {!isCurrent ? (
                        <button className="media-btn" disabled={actionLoading} onClick={() => handleCheckout(plan.id)}>
                          {t("pricing.subscribe")}
                        </button>
                      ) : (
                        <button className="media-btn secondary" disabled={actionLoading} onClick={handlePortal}>
                          {t("pricing.unsubscribe")}
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>
            {!plansError && plans.filter((plan) => plan.id === "starter" || plan.id === "pro").length === 0 && (
              <div className="pricing-empty">{t("pricing.noPlans")}</div>
            )}
          </section>

          <section className="pricing-topups">
            <h2>{t("pricing.topups")}</h2>
            {topupsError && <div className="pricing-inline-error">{topupsError}</div>}
            <div className="pricing-topup-grid">
              {topups.map((pack) => (
                <div key={pack.id} className="pricing-topup-card">
                  <div className="pricing-topup-head">
                    <h3>{pack.label}</h3>
                    {pack.label_discount ? (
                      <span className="pricing-discount">{pack.label_discount}</span>
                    ) : null}
                  </div>
                  <p>{pack.credits} {t("pricing.credits")}</p>
                  <button className="media-btn" disabled={actionLoading} onClick={() => handleTopup(pack.id)}>
                    {t("pricing.buy")}
                  </button>
                </div>
              ))}
            </div>
            {!topupsError && topups.length === 0 && (
              <div className="pricing-empty">{t("pricing.noTopups")}</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
